/**
 * sushiro は 2 つのオリジンで配信されている。
 *
 * - ubie.app         … ofro(medico) と同一オリジン。集患サポート(booking) / 薬局予約。
 * - inquiry.ubie.app … スマホ問診。
 *
 * localStorage はオリジンごとに独立しているため、ofro が書き込む `medico-user` と
 * medico 形状の `ubie-auth`（`{ authSession: { ... } }`）が存在するのは ubie.app 側だけ。
 * inquiry.ubie.app にも `ubie-auth` はあるが、それはスマホ問診自身が
 * 別の形状（`{ authAccountId, accessToken }`）で書いた値で、medico の情報ではない。
 *
 * `_app` が全ページを `MedicoUserAccount` でラップしている都合上、ofro 依存の処理は
 * inquiry.ubie.app でもマウントされてしまう。その取り違えを構造的に防ぐため、
 * ofro 依存の処理はこの判定で ubie.app オリジンに閉じる。
 */

/**
 * `origin` が ofro(medico) と同一オリジン（＝ubie.app 側）かどうか。
 *
 * @param origin 判定対象のオリジン。SSR など判定できない場合は `undefined` を渡す。
 */
export const isUbieAppOrigin = (origin: string | undefined): boolean => {
  // SSR ではオリジンが分からないため ubie.app 前提で描画し、クライアントで確定させる。
  // 差が出るのは「集患サポート / 薬局予約のパスを ubie.app 以外で開いた」場合だけ。
  if (origin == null) return true;

  // ローカルは sushiro(:3100) と ofro(:4000) を別オリジンで動かしているため判定を免除する
  // （ローカル向けの案内表示は MedicoUserAccount 側の local-setup で行う）
  if (process.env.NEXT_PUBLIC_APP_ENV === 'local') return true;

  const medicoUrl = process.env.NEXT_PUBLIC_MEDICO_URL;
  // 未設定は想定外（ofro へのリダイレクト自体が成立しない）。
  // ここで false にすると ubie.app 側の予約フローを丸ごと止めてしまうため、従来どおりの挙動に倒す。
  if (!medicoUrl) return true;

  try {
    return origin === new URL(medicoUrl).origin;
  } catch {
    return true;
  }
};

/** 現在のオリジンが ubie.app（ofro と同一オリジン）かどうか。 */
export const isCurrentUbieAppOrigin = (): boolean =>
  isUbieAppOrigin(typeof window === 'undefined' ? undefined : window.location.origin);
